import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import SearchBar from '../../components/common/SearchBar';
import Pagination from '../../components/common/Pagination';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import ParallaxTiltCard from '../../components/common/ParallaxTiltCard';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import { useAuth } from '../../context/AuthContext';
import { getProductList, deleteProduct, stopSale, resumeSale, reorderProducts, updateProductRecommend, getThumbnailImageUrl } from '../../api/product';
import { getCategoryList } from '../../api/category';
import { formatDateTime } from '../../utils/format';

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'ON_SALE', label: '판매중' },
  { value: 'STOPPED', label: '판매중단' },
];

export default function ProductListPage() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 10, saleStatus: '', categoryId: '' });
  const [page, setPage] = useState(null);
  const [rows, setRows] = useState([]); // page.list 를 드래그 중 로컬로 재정렬하기 위한 복사본
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stopTarget, setStopTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [smallCategories, setSmallCategories] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'table' | 'card' - 기본을 그리드형(카드)으로

  useEffect(() => {
    getCategoryList()
      .then((res) => setSmallCategories((res.data || []).filter((c) => c.categoryLevel === 3)))
      .catch(() => setSmallCategories([]));
  }, []);

  const fetchList = useCallback(async (cond) => {
    setLoading(true);
    setError('');
    try {
      const res = await getProductList(cond);
      setPage(res.data);
      setRows(res.data?.list || []);
    } catch (err) {
      setError(err.response?.data?.message || '상품 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);

  const reorderEnabled = !!condition.categoryId;

  const handleToggleSale = async () => {
    try {
      if (stopTarget.saleStatus === 'ON_SALE') {
        await stopSale(stopTarget.productId, auth.adminName);
      } else {
        await resumeSale(stopTarget.productId, auth.adminName);
      }
      setStopTarget(null);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '판매 상태 변경에 실패했습니다.');
    }
  };

  /** 추천메뉴 지정/해제 - 확인창 없이 목록에서 바로 토글한다. */
  const handleToggleRecommend = async (product) => {
    try {
      await updateProductRecommend(product.productId, product.recommendYn === 'Y' ? 'N' : 'Y', auth.adminName);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '추천메뉴 설정에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProduct(deleteTarget.productId);
      setDeleteTarget(null);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '상품 삭제에 실패했습니다.');
    }
  };

  // ---- 드래그앤드롭 노출순서 변경 (카테고리 필터가 선택되어 있을 때만 활성화) ----
  const handleDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  };

  const handleDrop = async () => {
    if (dragIndex === null) return;
    setDragIndex(null);
    setSavingOrder(true);
    try {
      await reorderProducts(rows.map((r) => r.productId), auth.adminName);
    } catch (err) {
      window.alert(err.response?.data?.message || '노출순서 저장에 실패했습니다.');
      fetchList(condition);
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <AdminLayout title="상품관리" menuUrl="/admin/product">
      <div className="page-header">
        <div>
          <h1>상품관리</h1>
          <div className="subtitle">대표 전시상품과 선택 옵션, 재고, 판매상태를 관리합니다.</div>
        </div>
      </div>

      <div className="list-toolbar">
        <SearchBar
          extraFilter={{
            options: [{ value: '', label: '전체 분류' }, ...smallCategories.map((c) => ({ value: String(c.categoryId), label: c.categoryName }))],
            value: condition.categoryId,
            onChange: (v) => setCondition((c) => ({ ...c, categoryId: v, pageNo: 1, pageSize: v ? 100 : 10 })),
          }}
          placeholder="상품명으로 검색"
          onSearch={(cond) => setCondition((c) => ({ ...c, ...cond }))}
        />
        <div className="list-toolbar-right">
          <button className="btn btn-primary" onClick={() => navigate('/products/new')}>+ 상품 등록</button>
        </div>
      </div>

      <div className="search-bar">
        <select className="select" value={condition.saleStatus} onChange={(e) => setCondition((c) => ({ ...c, saleStatus: e.target.value, pageNo: 1 }))}>
          {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>

        <div className="view-toggle">
          <button className={viewMode === 'table' ? 'active' : ''} onClick={() => setViewMode('table')}>목록형</button>
          <button className={viewMode === 'card' ? 'active' : ''} onClick={() => setViewMode('card')}>카드형</button>
        </div>

        {reorderEnabled && (
          <span className="text-secondary" style={{ fontSize: 12.5 }}>
            {savingOrder ? '순서 저장 중...' : (viewMode === 'card' ? '카드를 드래그하여 노출순서를 변경할 수 있습니다.' : '행을 드래그하여 노출순서를 변경할 수 있습니다.')}
          </span>
        )}
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      {viewMode === 'table' ? (
        <div className="card">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {reorderEnabled && <th style={{ width: 28 }}></th>}
                  <th>이미지</th><th>상품명</th><th>기준가격</th><th>총재고</th><th>판매상태</th><th>등록일</th><th>관리</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="empty-row">불러오는 중...</td></tr>}
                {!loading && error && <tr><td colSpan={8} className="empty-row error-state">{error}</td></tr>}
                {!loading && !error && rows.length === 0 && <tr><td colSpan={8} className="empty-row">등록된 상품이 없습니다.</td></tr>}
                {!loading && !error && rows.map((p, index) => (
                  <tr
                    key={p.productId}
                    draggable={reorderEnabled}
                    onDragStart={reorderEnabled ? handleDragStart(index) : undefined}
                    onDragOver={reorderEnabled ? handleDragOver(index) : undefined}
                    onDrop={reorderEnabled ? handleDrop : undefined}
                    style={reorderEnabled ? { cursor: 'grab' } : undefined}
                  >
                    {reorderEnabled && <td className="text-secondary">⠿</td>}
                    <td>
                      <ImageWithFallback
                        src={p.thumbnailImagePath ? getThumbnailImageUrl(p.productId) : null}
                        alt={p.productName}
                        style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }}
                      />
                    </td>
                    <td>{p.productName}{p.recommendYn === 'Y' && <span className="recommend-badge">추천</span>}</td>
                    <td>{Number(p.basePrice).toLocaleString()}원</td>
                    <td>{Number(p.totalStock).toLocaleString()}</td>
                    <td><StatusBadge value={p.saleStatus} /></td>
                    <td>{formatDateTime(p.createdAt)}</td>
                    <td className="actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/products/${p.productId}/edit`)}>수정</button>
                      <button className={`btn btn-sm ${p.recommendYn === 'Y' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleToggleRecommend(p)}>
                        {p.recommendYn === 'Y' ? '추천 해제' : '추천 지정'}
                      </button>
                      <button className={`btn btn-sm ${p.saleStatus === 'ON_SALE' ? 'btn-danger' : 'btn-secondary'}`} onClick={() => setStopTarget(p)}>
                        {p.saleStatus === 'ON_SALE' ? '판매중단' : '판매재개'}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(p)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          {loading && <div className="card"><div className="empty-row">불러오는 중...</div></div>}
          {!loading && error && <div className="card"><div className="empty-row error-state">{error}</div></div>}
          {!loading && !error && rows.length === 0 && <div className="card"><div className="empty-row">등록된 상품이 없습니다.</div></div>}
          {!loading && !error && rows.length > 0 && (
            <div className="product-card-grid">
              {rows.map((p, index) => (
                <ParallaxTiltCard
                  key={p.productId}
                  className={`product-card${reorderEnabled ? ' draggable' : ''}`}
                  maxTilt={6}
                  draggable={reorderEnabled}
                  onDragStart={reorderEnabled ? handleDragStart(index) : undefined}
                  onDragOver={reorderEnabled ? handleDragOver(index) : undefined}
                  onDrop={reorderEnabled ? handleDrop : undefined}
                >
                  <div className="product-card-thumb">
                    {reorderEnabled && <span className="drag-handle">⠿</span>}
                    <span className="product-card-status"><StatusBadge value={p.saleStatus} /></span>
                    <ImageWithFallback
                      src={p.thumbnailImagePath ? getThumbnailImageUrl(p.productId) : null}
                      alt={p.productName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </div>
                  <div className="product-card-body">
                    {p.categoryName && <div className="product-card-category">{p.categoryName}</div>}
                    <div className="product-card-name" title={p.productName}>
                      {p.productName}{p.recommendYn === 'Y' && <span className="recommend-badge">추천</span>}
                    </div>
                    <div className="product-card-price">{Number(p.basePrice).toLocaleString()}원</div>
                    <div className="product-card-stock">재고 {Number(p.totalStock).toLocaleString()}개</div>
                    <div className="product-card-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/products/${p.productId}/edit`)}>수정</button>
                      <button className={`btn btn-sm ${p.recommendYn === 'Y' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleToggleRecommend(p)}>
                        {p.recommendYn === 'Y' ? '추천 해제' : '추천 지정'}
                      </button>
                      <button className={`btn btn-sm ${p.saleStatus === 'ON_SALE' ? 'btn-danger' : 'btn-secondary'}`} onClick={() => setStopTarget(p)}>
                        {p.saleStatus === 'ON_SALE' ? '판매중단' : '판매재개'}
                      </button>
                    </div>
                  </div>
                </ParallaxTiltCard>
              ))}
            </div>
          )}
        </>
      )}

      {page && !reorderEnabled && <Pagination {...page} onChange={(pageNo) => setCondition((c) => ({ ...c, pageNo }))} />}

      {stopTarget && (
        <ConfirmDialog
          title={stopTarget.saleStatus === 'ON_SALE' ? '판매중단' : '판매재개'}
          message={stopTarget.saleStatus === 'ON_SALE'
            ? `'${stopTarget.productName}' 상품을 즉시 판매중단 처리하시겠습니까?`
            : `'${stopTarget.productName}' 상품 판매를 재개하시겠습니까?`}
          confirmLabel={stopTarget.saleStatus === 'ON_SALE' ? '판매중단' : '판매재개'}
          danger={stopTarget.saleStatus === 'ON_SALE'}
          onCancel={() => setStopTarget(null)}
          onConfirm={handleToggleSale}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="상품 삭제"
          message={`'${deleteTarget.productName}' 상품과 모든 옵션/이미지를 삭제하시겠습니까? 되돌릴 수 없습니다.`}
          confirmLabel="삭제"
          danger
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </AdminLayout>
  );
}
